import React, { useCallback, useEffect, useState } from 'react';
import { SkeletonRows } from '../ui/Skeleton';
import { PageHeader } from '../ui/PageHeader';
import { ErrorBanner } from '../ui/ErrorBanner';
import { supabase } from '../lib/supabase';
import { useToast } from '../lib/useToast';
import { ConfirmModal } from '../components/ConfirmModal';

type DuplicatePair = {
  a_id: string;
  a_name: string;
  a_rating_count: number;
  a_fsq: string | null;
  a_apple: string | null;
  b_id: string;
  b_name: string;
  b_rating_count: number;
  b_fsq: string | null;
  b_apple: string | null;
  distance_m: number;
  name_core: string;
  suggested_keep: string;
};

type ConfirmState = {
  keepId: string;
  dropId: string;
  keepName: string;
  dropName: string;
} | null;

function KeyBadge({ label, present }: { label: string; present: boolean }) {
  if (!present) return null;
  return (
    <span className="rounded-md border border-violet-500/40 bg-violet-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-200">
      {label}
    </span>
  );
}

function VenueCard({
  id,
  name,
  ratingCount,
  fsq,
  apple,
  isSuggested,
  onMergeKeepThis,
  merging,
}: {
  id: string;
  name: string;
  ratingCount: number;
  fsq: string | null;
  apple: string | null;
  isSuggested: boolean;
  onMergeKeepThis: (keepId: string) => void;
  merging: boolean;
}) {
  return (
    <div
      className={`flex flex-col gap-3 rounded-xl border p-4 flex-1 min-w-0 ${
        isSuggested
          ? 'border-accent/50 bg-accent/10'
          : 'border-border bg-surface'
      }`}
    >
      {isSuggested && (
        <span className="self-start rounded-full border border-accent/50 bg-accent/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-accent">
          Suggested keep
        </span>
      )}
      <div className="min-w-0">
        <p className="admin-label-primary truncate" title={name}>
          {name}
        </p>
        <p className="text-sm text-muted mt-1 tabular-nums">
          {ratingCount === 1 ? '1 rating' : `${ratingCount} ratings`}
        </p>
      </div>
      <div className="flex flex-wrap gap-1.5">
        <KeyBadge label="fsq" present={Boolean(fsq)} />
        <KeyBadge label="apple" present={Boolean(apple)} />
        {!fsq && !apple && (
          <span className="text-[10px] text-mutedDim">no external key</span>
        )}
      </div>
      <p className="text-[11px] text-mutedDim break-all">
        id: {id.slice(0, 8)}…
      </p>
      <button
        type="button"
        disabled={merging}
        onClick={() => onMergeKeepThis(id)}
        className={`mt-auto rounded-lg border px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-50 ${
          isSuggested
            ? 'border-accent/60 bg-accent/20 text-white hover:bg-accent/30'
            : 'border-border text-muted hover:bg-white/[0.05] hover:text-white'
        }`}
      >
        Merge → keep this
      </button>
    </div>
  );
}

export function DuplicatesScreen() {
  const [pairs, setPairs] = useState<DuplicatePair[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [merging, setMerging] = useState(false);
  const { showSuccess, showError, toastNode } = useToast();

  const pairKey = (p: DuplicatePair) => `${p.a_id}:${p.b_id}`;

  const loadPairs = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data, error } = await supabase.rpc('admin_list_duplicate_venue_pairs', {
        p_limit: 100,
      });
      if (error) throw error;
      setPairs((data as DuplicatePair[]) ?? []);
      setDismissed(new Set());
    } catch (e: unknown) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load duplicate pairs');
      setPairs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPairs();
  }, [loadPairs]);

  function requestMerge(pair: DuplicatePair, keepId: string) {
    const keepName = keepId === pair.a_id ? pair.a_name : pair.b_name;
    const dropId = keepId === pair.a_id ? pair.b_id : pair.a_id;
    const dropName = keepId === pair.a_id ? pair.b_name : pair.a_name;
    setConfirm({ keepId, dropId, keepName, dropName });
  }

  async function executeMerge() {
    if (!confirm) return;
    setMerging(true);
    try {
      const { error } = await supabase.rpc('admin_merge_venues', {
        p_keep_id: confirm.keepId,
        p_drop_id: confirm.dropId,
      });
      if (error) throw error;
      showSuccess(`Merged — kept "${confirm.keepName}", deleted "${confirm.dropName}"`);
      // Remove all pairs that involved either venue from the list
      const { keepId, dropId } = confirm;
      setPairs((prev) =>
        prev.filter((p) => p.a_id !== keepId && p.b_id !== keepId && p.a_id !== dropId && p.b_id !== dropId),
      );
      setConfirm(null);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Merge failed');
    } finally {
      setMerging(false);
    }
  }

  function dismiss(key: string) {
    setDismissed((prev) => { const next = new Set(Array.from(prev)); next.add(key); return next; });
  }

  const visible = pairs.filter((p) => !dismissed.has(pairKey(p)));

  return (
    <div className="admin-page max-w-5xl mx-auto">
      <PageHeader
        title="Duplicates"
        subtitle="Venue pairs with the same normalized name within 80 m. Merge folds all ratings into the kept venue and recomputes its score. This action is irreversible."
        actions={
          <button
            type="button"
            onClick={() => void loadPairs()}
            disabled={loading}
            className="admin-btn admin-btn-secondary admin-btn-sm"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        }
      />

      {loadError ? (
        <ErrorBanner className="mb-4">{loadError}</ErrorBanner>
      ) : loading ? (
        <SkeletonRows rows={6} className="py-4" />
      ) : pairs.length === 0 ? (
        <div className="rounded-xl border border-border bg-surface px-6 py-12 text-center">
          <p className="text-muted text-sm">No duplicate venue pairs found.</p>
          <p className="text-mutedDim text-xs mt-2">
            The RPC scans for same-name venues within 80 m — run it again after new venue data is
            ingested.
          </p>
        </div>
      ) : (
        <>
          <p className="text-xs text-mutedDim mb-6">
            {pairs.length} pair{pairs.length !== 1 ? 's' : ''} found
            {dismissed.size > 0 ? ` · ${dismissed.size} skipped this session` : ''}
            {visible.length < pairs.length && dismissed.size > 0
              ? ` · ${visible.length} remaining`
              : ''}
          </p>

          {visible.length === 0 ? (
            <div className="rounded-xl border border-border bg-surface px-6 py-12 text-center">
              <p className="text-muted text-sm">
                All pairs skipped for this session.
              </p>
              <button
                type="button"
                onClick={() => void loadPairs()}
                className="mt-4 rounded-lg border border-border px-4 py-2 text-sm text-muted hover:bg-white/[0.05] hover:text-white transition-colors"
              >
                Reload
              </button>
            </div>
          ) : (
            <ul className="space-y-4">
              {visible.map((pair) => {
                const key = pairKey(pair);
                return (
                  <li
                    key={key}
                    className="rounded-xl border border-border bg-rowAlt overflow-hidden"
                  >
                    {/* Header row */}
                    <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border/60">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-mutedDim">
                          Core name
                        </span>
                        <span className="text-sm text-white/90 truncate">{pair.name_core}</span>
                        <span className="rounded-md border border-border px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-mutedDim">
                          {Math.round(pair.distance_m)} m apart
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => dismiss(key)}
                        className="shrink-0 rounded-md border border-border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-mutedDim hover:text-white transition-colors"
                      >
                        Skip
                      </button>
                    </div>

                    {/* Side-by-side venue cards */}
                    <div className="flex gap-3 p-4 flex-col sm:flex-row">
                      <VenueCard
                        id={pair.a_id}
                        name={pair.a_name}
                        ratingCount={pair.a_rating_count}
                        fsq={pair.a_fsq}
                        apple={pair.a_apple}
                        isSuggested={pair.suggested_keep === pair.a_id}
                        onMergeKeepThis={(keepId) => requestMerge(pair, keepId)}
                        merging={merging}
                      />
                      <div className="flex sm:flex-col items-center justify-center gap-1 shrink-0 py-2">
                        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-mutedDim">
                          vs
                        </span>
                      </div>
                      <VenueCard
                        id={pair.b_id}
                        name={pair.b_name}
                        ratingCount={pair.b_rating_count}
                        fsq={pair.b_fsq}
                        apple={pair.b_apple}
                        isSuggested={pair.suggested_keep === pair.b_id}
                        onMergeKeepThis={(keepId) => requestMerge(pair, keepId)}
                        merging={merging}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      <ConfirmModal
        open={confirm !== null}
        title="Merge venues?"
        message={
          confirm
            ? `Keep "${confirm.keepName}", delete "${confirm.dropName}"? This re-points all ratings and can't be undone.`
            : ''
        }
        confirmLabel="Merge"
        danger
        busy={merging}
        onConfirm={() => void executeMerge()}
        onCancel={() => !merging && setConfirm(null)}
      />

      {toastNode}
    </div>
  );
}
